import React from "react"
import PropTypes from "prop-types"
import { HAS_TOUCH } from "AtomicUtils/environment"
import { InfoIcon } from "AtomicComponents"
import { Image } from "AtomicContainers"
import {
  BackgroundImage,
  Overlay,
  FiftyFiftyBlock,
  ImageDisclaimer,
  TextMediaSliderContainer,
  VideoOverlay
} from "containers"
import { PortalWithState } from "react-portal"

/** Render a gallery item */
/**
 * @param {String} [imageSrc] - URL for image asset.
 * @param {String} [overLayImageSrc] - URL for image asset on the overlay.
 * @param {Array} [overLayImagesArray] - Array of images for the overlay with transitions
 * @param {Number} [interval] - Interval (in ms) in-between image transitions
 * @param {Boolean} [loop] - Flag if the transition should loop or not
 * @param {String} [videoId] - Youtube video ID. If specified the gallery item will display video.
 * @param {String} title - Heading text.
 * @param {String} [blurb] - blurb text as xhtml string.
 * @param {Boolean} [disclaimerUppercase=true] - Display disclaimer as all uppercase.
 * @param {Function} [onClick] - Callback to run on button click.
 * @param {Array|Button} [components] - Array of nested components.
 * @returns {ReactElement} React Component
 */
const GalleryItem = ({
  imageSrc,
  overLayImageSrc,
  overLayImagesArray,
  interval,
  loop,
  videoId,
  title,
  blurb,
  additionalStyles = {},
  disclaimerUppercase,
  active,
  fade,
  slideShowItems,
  index,
  handleMouseEnter,
  handleMouseLeave,
  compact,
  components
}) => (
    <PortalWithState>
      {({ openPortal, closePortal, portal }) => [
        <a
          key={imageSrc}
          style={{ ...styles.figure(fade), ...additionalStyles }}
          data-test="gallery-item"
          onClick={openPortal}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          <span tabIndex={0} style={styles.href(active)}>
            <BackgroundImage position="absolute" src={imageSrc} />
            <Image type="blurred" src={imageSrc} alt={title} style={styles.img} />
          </span>
          <ImageDisclaimer
            textAlign={compact ? "right" : "left"}
            size="small"
            uppercase={disclaimerUppercase}
            content={compact ? <InfoIcon style={{width: "15px", height: "15px"}} /> : title}
            active={active} />
        </a>,
        portal(videoId ?
          <VideoOverlay videoId={videoId} close={closePortal} />
          :
          <Overlay close={closePortal}>
            {
              compact ?
              <TextMediaSliderContainer
                bulletsBelow
                displayOnTop
                padding={false}
                active={index}
                items={slideShowItems} />
              :
              <FiftyFiftyBlock
                heading={title}
                blurb={blurb}
                textAlign="center"
                headingUppercase={disclaimerUppercase}
                textVerticalAlign="center"
                backgroundImageArray={overLayImagesArray}
                interval={interval}
                overlay
                loop={loop}
                imageUrl={overLayImageSrc || imageSrc}
                components={components} />
            }
          </Overlay>)
      ]}
    </PortalWithState>
)

GalleryItem.propTypes = {
  title: PropTypes.string,
  blurb: PropTypes.string,
  imageSrc: PropTypes.string,
  overLayImageSrc: PropTypes.string,
  overLayImagesArray: PropTypes.array,
  interval: PropTypes.number,
  loop: PropTypes.bool,
  videoId: PropTypes.string,
  onClick: PropTypes.func,
  disclaimerUppercase: PropTypes.bool,
  additionalStyles: PropTypes.object,
  active: PropTypes.bool,
  fade: PropTypes.bool,
  handleMouseEnter: PropTypes.func,
  handleMouseLeave: PropTypes.func,
  slideShowItems: PropTypes.array,
  index: PropTypes.number,
  compact: PropTypes.bool,
  components: PropTypes.array
}

GalleryItem.defaultProps = {
  disclaimerUppercase: true
}

GalleryItem.displayName = "GalleryItem"

export default GalleryItem

const styles = {
  figure: fade => ({
    display: "block",
    margin: 0,
    position: "absolute",
    left: 0,
    top: 0,
    height: "100%",
    width: "100%",
    overflow: "hidden",
    transform: "translate3d(0,0,0) scale(1.005)",
    transition: !HAS_TOUCH && "filter .5s",
    filter: `brightness(${fade ? "0.3" : "1"})`,
    color: "white"
  }),
  img: {
    height: "100%",
    width: "100%",
    position: "relative",
    zIndex: 2,
    opacity: 0
  },
  slideshowItem: {
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    overflow: "hidden" // Fix overflow problem in IE11
  },
  href: active => ({
    cursor: "pointer",
    display: "block",
    height: "100%",
    width: "100%",
    outline: 0,
    transform: `scale(${!HAS_TOUCH && active ? "1.2" : "1"})`,
    transition: !HAS_TOUCH && "transform 0.5s"
  })
}



// WEBPACK FOOTER //
// ./src/containers/gallery-item/index.jsx